import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Container,
  Grid,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
} from "@mui/material";
import MeetingsList from "./MeetingsList";
import { EXHIBITOR } from "../../../../constants/constants";
import TeamMeetingsList from "./TeamMeetingsList";
import DateRangeIcon from "@mui/icons-material/DateRange";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupsIcon from "@mui/icons-material/Groups";
import useApiEp from "../../../../hooks/useApiEp";
import { UserContext } from "../../../context/UserContext";
import BackBtn from "../../../../widgets/BackBtn";
import PageHeader from "../../common/PageHeader";

const statusOptions = [
  { label: "All", value: "", color: "primary" },
  { label: "Accepted", value: "accepted", color: "success" },
  { label: "Cancelled", value: "cancelled", color: "error" },
];

const MeetingStatCard = ({
  title,
  count,
  icon,
  color,
  isLoading,
  onClick,
  active,
}) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      border: "1.5px solid",
      borderColor: active ? alpha(color, 0.5) : "#eef2f6",
      background: active
        ? `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(
            color,
            0.03
          )} 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #fafcff 100%)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: `0px 12px 28px -8px ${alpha(color, 0.22)}`,
        borderColor: alpha(color, 0.4),
      },
    }}
  >
    <CardActionArea onClick={onClick} sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              fontSize: "0.68rem",
              display: "block",
              mb: 0.75,
            }}
          >
            {title}
          </Typography>
          {isLoading ? (
            <Skeleton
              variant="text"
              width={60}
              height={44}
              sx={{ borderRadius: 1 }}
            />
          ) : (
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: active ? color : "#0f172a",
                lineHeight: 1.1,
                letterSpacing: "-0.5px",
              }}
            >
              {count ?? 0}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            background: `linear-gradient(135deg, ${alpha(
              color,
              0.12
            )} 0%, ${alpha(color, 0.06)} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 26, color } })}
        </Box>
      </Box>
      {active && (
        <Box
          sx={{
            mt: 1.5,
            height: 3,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${color}, ${alpha(
              color,
              0.4
            )})`,
          }}
        />
      )}
    </CardActionArea>
  </Card>
);

const AllMeetings = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [status, setStatus] = useState("");
  const { user, loading } = useContext(UserContext);

  const { data: meetingRequests, isLoading: isLoadingRequests } = useApiEp(
    `/user/get-meetings?status=requested&type=`
  );
  const { data: myMeetings, isLoading: isLoadingMy } = useApiEp(
    `/user/get-meetings?status=&type=`
  );
  const { data: teamMeetings, isLoading: isLoadingTeam } = useApiEp(
    `/user/get-team-meetings?status=`
  );

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (tabIndex === 0) {
      setStatus("requested");
    }
    if (tabIndex === 1) {
      setStatus("");
    }
  }, [tabIndex]);

  const handleStatusChange = (statusValue) => {
    setStatus(statusValue);
  };

  const meetingType = tabIndex === 0 ? "received" : "sent";
  const isExhibitor = user?.role === EXHIBITOR;

  const statsCards = [
    {
      title: "Meeting Requests",
      count: meetingRequests?.length,
      icon: <DateRangeIcon />,
      color: "#6366f1",
      isLoading: isLoadingRequests,
      tabTarget: 0,
    },
    {
      title: "My Meetings",
      count: myMeetings?.length,
      icon: <EventAvailableIcon />,
      color: "#10b981",
      isLoading: isLoadingMy,
      tabTarget: 1,
    },
    ...(isExhibitor
      ? [
          {
            title: "Team Meetings",
            count: teamMeetings?.length,
            icon: <GroupsIcon />,
            color: "#f59e0b",
            isLoading: isLoadingTeam,
            tabTarget: 2,
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <BackBtn />
      <Container maxWidth="lg">
        <PageHeader
          title="B2B Meetings"
          subtitle="Schedule, track, and manage your one-on-one event meetings and requests"
          icon={EventAvailableIcon}
        />
        {/* Stats Cards */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {statsCards.map((card, idx) => (
            <Grid item xs={12} sm={isExhibitor ? 4 : 6} key={idx}>
              <MeetingStatCard
                title={card.title}
                count={card.count}
                icon={card.icon}
                color={card.color}
                isLoading={card.isLoading}
                active={tabIndex === card.tabTarget}
                onClick={() => setTabIndex(card.tabTarget)}
              />
            </Grid>
          ))}
        </Grid>

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid #e2e8f0",
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              backgroundColor: "#2563eb",
            },
          }}
        >
          <Tab
            label="Meeting Requests"
            sx={{
              textTransform: "none",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 600,
              color: "#64748b",
              px: 3,
              py: 2,
              "&.Mui-selected": { color: "#0f172a", fontWeight: 800 },
              "&:hover": { color: "#2563eb" },
            }}
          />
          <Tab
            label="My Meetings"
            sx={{
              textTransform: "none",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 600,
              color: "#64748b",
              px: 3,
              py: 2,
              "&.Mui-selected": { color: "#0f172a", fontWeight: 800 },
              "&:hover": { color: "#2563eb" },
            }}
          />
          {isExhibitor && (
            <Tab
              label="Team Member Meetings"
              sx={{
                textTransform: "none",
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 600,
                color: "#64748b",
                px: 3,
                py: 2,
                "&.Mui-selected": { color: "#0f172a", fontWeight: 800 },
                "&:hover": { color: "#2563eb" },
              }}
            />
          )}
        </Tabs>

        {status !== "requested" && (
          <Stack
            maxWidth={800}
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              mt: 2,
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            {statusOptions.map((option) => (
              <Button
                key={option.value}
                variant={status === option.value ? "contained" : "outlined"}
                color={option.color}
                onClick={() => handleStatusChange(option.value)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "20px",
                  px: 3,
                  boxShadow:
                    status === option.value
                      ? "0 4px 10px rgba(0,0,0,0.1)"
                      : "none",
                }}
              >
                {option.label}
              </Button>
            ))}
          </Stack>
        )}

        <Box sx={{ mt: 2, px: { xs: 2, sm: 0 } }}>
          {tabIndex < 2 ? (
            <MeetingsList type={""} status={status} />
          ) : (
            <TeamMeetingsList status={status} />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default AllMeetings;
